import type { BidHistoryItem } from '@tempmee/awards';
import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useBidHistoryQuery } from './useGetBidHistory';

export interface IEligibleBidNominationResult {
  /**
   * The office's apply-eligible Best-in-Dental nomination, or `null` when none
   * applies (not a nominee, window closed, flag off, or still loading).
   */
  eligibleItem: BidHistoryItem | null;
  isLoading: boolean;
}

/**
 * Resolves the office's current apply-eligible Best-in-Dental nomination
 * (GOTU-6482 dashboard tile, GOTU-5642 nudge popup). Centralises the
 * eligibility rule — a `nominee` membership whose cycle is still inside its
 * application window — so both surfaces stay in lockstep.
 *
 * `applyEligible` is server-derived (status + window); we never re-derive the
 * window from the cycle dates client-side. Gated on the BID v2 flag and shares
 * the page-1 history query cache with the BID page + visibility gate, so a
 * consumer never triggers its own fetch.
 */
export const useEligibleBidNomination = (): IEligibleBidNominationResult => {
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const query = useBidHistoryQuery(1, isFlagOn);

  const eligibleItem =
    query.data?.items.find((item) => item.membershipStatus === 'nominee' && item.applyEligible) ??
    null;

  return {
    eligibleItem: isFlagOn ? eligibleItem : null,
    isLoading: isFlagOn && query.isLoading,
  };
};
